<template>
  <ontos-instance-card v-if="!loaded">
    <ontos-dialog-loading/>
  </ontos-instance-card>
  <ontos-instance-card v-else>
    <v-card-title>
      <ontos-page-header :title="title" :subtitle="subtitle" :accent="accent" :level="2">
        <!-- As ações sobre o item, quando quem hospeda tem alguma (o Design da página de editar):
             o bloco do PageHeader -->
        <template v-if="$slots.actions" #actions>
          <slot name="actions"/>
        </template>
        <!-- Os controles vêm de fora: fechar, expandir e voltar são decisões da JANELA, e ela
             muda conforme o que está mostrando. O painel não sabe para onde se sai dele. -->
        <template v-if="$slots.controls" #controls>
          <slot name="controls"/>
        </template>
      </ontos-page-header>
    </v-card-title>
    <v-card-text>
      <slot/>
    </v-card-text>
    <ontos-dialog-action-row>
      <!-- Outros botões de gravar, antes do Salvar — o "Salvar e criar outro" da criação -->
      <slot name="more-actions"/>
      <v-btn size="small" color="primary" :loading="saving" :disabled="saving || saveDisabled"
             @click="$emit('save')">
        <v-icon size="small" start>mdi-floppy</v-icon>
        {{ $tt('save') }}
      </v-btn>
    </ontos-dialog-action-row>
  </ontos-instance-card>

  <!-- Irmão do card, e não filho — e é por isso que este painel tem DUAS raízes. O porquê está
       no próprio BusyOverlay, junto do raciocínio que o decidiu.

       **Consequência para quem usa este painel**: ele precisa ser desenhado DIRETO dentro do
       `v-dialog`. Embrulhá-lo numa div devolveria o problema, porque a div passaria a ser o
       ancestral posicionado. -->
  <ontos-busy-overlay :model="saving"/>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import BusyOverlay from '@/components/shared/BusyOverlay.vue'
import DialogActionRow from '@/components/shared/DialogActionRow.vue'
import InstanceCard from '@/components/shared/InstanceCard.vue'
import DialogLoading from '@/components/shared/DialogLoading.vue'

/**
 * O miolo de um modal de formulário: cabeçalho, conteúdo e um único botão Salvar no rodapé
 * — **sem** o `v-dialog` em volta.
 *
 * Existe porque uma janela pode mostrar um formulário sem ser uma janela NOVA: a da instância
 * troca entre ver e editar dentro do mesmo diálogo, e um `v-dialog` aninhado ali seria
 * exatamente o empilhamento que se quer evitar. O `FormDialog` passa a ser este painel mais a
 * janela, então quem já o usava não percebe a diferença.
 *
 * Quem valida e grava é o host: daqui sai só o evento `save`.
 */
@Component({emits: ['save'],
  name: 'ontos-form-panel',
  //Síncrono de propósito: um indicador de carga assíncrono demoraria a aparecer
  components: {
    'ontos-busy-overlay': BusyOverlay,
    'ontos-dialog-action-row': DialogActionRow,
    'ontos-dialog-loading': DialogLoading,
    'ontos-instance-card': InstanceCard
  }
})
class FormPanel extends Vue {
  @Prop({type: String, default: ''}) readonly title!: string
  @Prop({type: String, default: ''}) readonly subtitle!: string
  /** A cor do item, quando há: a régua ao lado do título, a mesma que a ficha dele mostra. */
  @Prop({type: String, default: ''}) readonly accent!: string
  /** Enquanto false, mostra o indicador de carga no lugar do formulário */
  @Prop({type: Boolean, default: true}) readonly loaded!: boolean
  @Prop({type: Boolean, default: false}) readonly saving!: boolean
  @Prop({type: Boolean, default: false}) readonly saveDisabled!: boolean
}

export default toNative(FormPanel)
</script>
